import { useQuery } from '@tanstack/react-query';
import { Loader2, AlertCircle, ArrowLeft, ExternalLink } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';

import { FeedbackActionsPanel } from '../../components/feedback/FeedbackActionsPanel';
import { FeedbackConversation } from '../../components/feedback/FeedbackConversation';
import { FeedbackStatusPanel } from '../../components/feedback/FeedbackStatusPanel';
import { FeedbackSummaryCard } from '../../components/feedback/FeedbackSummaryCard';
import { FeedbackTriagePanel } from '../../components/feedback/FeedbackTriagePanel';
import { getFeedbackDetail } from '../../services/api/adminFeedback';

import { useFeedbackActions } from './hooks/useFeedbackActions';

export const FeedbackDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-feedback-detail', id],
    queryFn: () => getFeedbackDetail(id!),
    enabled: !!id,
  });

  const {
    actionError,
    actionSuccess,
    statusMutation,
    triageMutation,
    knowledgeMutation,
    evalMutation,
    githubMutation,
    isRunning,
  } = useFeedbackActions(id!);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-brand-blue" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center gap-3 rounded-sm border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
        <AlertCircle className="h-5 w-5 flex-shrink-0" />
        <span>Kunde inte ladda feedback: {(error as Error)?.message || 'Okänt fel'}</span>
      </div>
    );
  }

  const { feedback, triageResult, conversationMessages } = data;

  return (
    <section className="flex h-full flex-col gap-6">
      <header className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate('/feedback')}
          className="rounded-sm border border-black/10 p-2 text-text-grey transition-colors hover:border-brand-blue hover:text-brand-blue"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <h2 className="text-3xl font-display text-brand-blue">Feedback Detail</h2>
          <p className="text-sm text-text-grey mt-1">
            Granska feedback, kör triage och utför åtgärder.
          </p>
        </div>
      </header>

      {actionSuccess ? (
        <div className="rounded-sm border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {actionSuccess}
        </div>
      ) : null}

      {actionError ? (
        <div className="rounded-sm border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          {actionError}
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-6">
          <FeedbackSummaryCard feedback={feedback} />

          <div className="mb-3">
            <button
              type="button"
              onClick={() => navigate(`/sessions?session=${feedback.conversationId}`)}
              className="inline-flex items-center gap-1 text-xs text-brand-blue hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Öppna sessionen
            </button>
          </div>

          <FeedbackConversation messages={conversationMessages} />
          <FeedbackTriagePanel triage={triageResult} />
        </div>

        <div className="space-y-4">
          <FeedbackActionsPanel
            feedback={feedback}
            isRunning={isRunning}
            triage={triageMutation}
            knowledge={knowledgeMutation}
            evalCase={evalMutation}
            github={githubMutation}
          />

          <FeedbackStatusPanel
            status={feedback.status}
            disabled={isRunning}
            isSaving={statusMutation.isPending}
            savingTo={statusMutation.variables}
            onChange={(status) => statusMutation.mutate(status)}
          />
        </div>
      </div>
    </section>
  );
};
